import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { useAuth } from "@/hooks/useAuth"
import {
  useActiveOrganization,
  useDeleteOrganization,
  useOnOrganizationEvent,
  useOrganizations,
  useSwitchOrganization,
} from "@/hooks/useOrganization"
import { useToast } from "@/hooks/useToast"
import { DeleteConfirmationDialog } from "@/components/ui/delete-confirmation-dialog"

export default function GeneralSettings() {
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const { user } = useAuth()
  const activeOrganization = useActiveOrganization()
  const organizations = useOrganizations()
  const { toast } = useToast()

  const userName = user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : ""
  const userEmail = user?.email || ""

  const switchOrganizationMutation = useSwitchOrganization()
  const onOrganizationEvent = useOnOrganizationEvent()

  const deleteOrganizationMutation = useDeleteOrganization({
    onSuccess: () => {
      toast({
        title: "Organization deleted",
        description: "The organization has been successfully deleted.",
      })
      onOrganizationEvent()
    },
    onError: (error) => {
      toast({
        title: "Delete failed",
        description:
          error?.response?.data?.error || error?.message || "Failed to delete organization. Please try again.",
        variant: "destructive",
      })
    },
    onSettled: () => {
      setShowDeleteDialog(false)
    },
  })

  const handleDeleteOrganization = async () => {
    if (!activeOrganization?.id) return

    try {
      // Find other organizations (excluding the one being deleted)
      const otherOrganizations = organizations.filter((org) => org.id !== activeOrganization.id)

      // If deleting the active organization and other organizations exist, switch first
      // This is necessary because the JWT token is tied to the active organization
      if (otherOrganizations.length > 0) {
        // Switch to the first available organization before deleting
        await switchOrganizationMutation.mutateAsync(otherOrganizations[0].id)
      }

      // Now delete the organization
      await deleteOrganizationMutation.mutateAsync(activeOrganization.id)
    } catch (error) {
      // Error handling is done in the mutation callbacks
      console.error("Failed to delete organization:", error)
    }
  }

  return (
    <div className="space-y-6">
      <div className="pl-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">General Settings</h1>
        <p className="mt-1 text-gray-600 dark:text-gray-400">Manage your account preferences and security settings.</p>
      </div>

      <div className="space-y-6 px-4 pb-12">
        {/* Account Information */}
        <div className="rounded-xl border border-gray-200 bg-white px-6 py-4 dark:border-gray-800 dark:bg-default">
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Account Information</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Your account details (contact support to update)
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Input
                  label="Full Name"
                  value={userName}
                  disabled
                  className="border-gray-200 bg-gray-50 dark:border-[#2B2B30] dark:bg-gray-800"
                />
              </div>

              <div>
                <Input
                  label="Email Address"
                  value={userEmail}
                  disabled
                  className="border-gray-200 bg-gray-50 dark:border-[#2B2B30] dark:bg-gray-800"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-[#1F1F23] dark:bg-variant">
          <div className="space-y-6">
            <div>
              <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">Security Settings</h3>
            </div>

            <div>
              <Input type="password" label="Current Password" />
            </div>

            <div>
              <Input
                type="password"
                label="New Password"
                className="border-gray-200 bg-white dark:border-[#2B2B30] dark:bg-variant"
              />
            </div>

            <div>
              <Input
                type="password"
                label="Confirm New Password"
                className="border-gray-200 bg-white dark:border-[#2B2B30] dark:bg-variant"
              />
            </div>

            <div className="flex items-center space-x-3">
              <Switch checked={twoFactorEnabled} onCheckedChange={setTwoFactorEnabled} />
              <label className="text-sm font-medium text-gray-900 dark:text-white">
                Enable Two-Factor Authentication
              </label>
            </div>

            <Button className="bg-gray-900 text-white hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100">
              Save Security Settings
            </Button>
          </div>
        </div>

        {/* Delete Organization */}
        <div className="rounded-xl border border-red-200 bg-white p-6 dark:border-red-800 dark:bg-variant">
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium text-red-600 dark:text-red-400">Danger Zone</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Permanently delete your organization. This action cannot be undone.
              </p>
            </div>

            <Button variant="destructive" onClick={() => setShowDeleteDialog(true)} disabled={!activeOrganization}>
              Delete Organization
            </Button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Delete Organization"
        description="Are you sure you want to delete {itemName}? This action cannot be undone and will permanently remove:"
        itemName={activeOrganization?.name || ""}
        onConfirm={handleDeleteOrganization}
        isDeleting={deleteOrganizationMutation.isPending || switchOrganizationMutation.isPending}
        confirmButtonText="Delete Organization"
        itemsToDelete={[
          "The organization and all its data",
          "All resources, integrations and connections",
          "All team members and their access",
          "All chat conversations and threads",
          "All billing and subscription information",
        ]}
      />
    </div>
  )
}
